import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { deleteAttachment } from "../../lib/api";
import {
  attachmentId,
  emptyJobDraft,
  validateJobDraft,
  type JobDraft,
  type JobTimingDraft,
} from "./lib/jobForm";

export interface UseJobFormOptions {
  /** Seeds the draft when editing an existing job card. */
  initialValue?: Partial<JobDraft>;
  /** Persisted job id — supplied when editing, so attachment deletes hit the API. */
  jobId?: string;
  /**
   * Called with the validated draft (and the staged `File` objects, keyed by
   * attachment id) when the form is submitted. The host owns uploading: the
   * job row must exist before files can be attached to it.
   */
  onSubmit: (draft: JobDraft, files: Map<string, File>) => void | Promise<void>;
  /**
   * Full-draft validation used as the submit gate. Defaults to the base job
   * validation; a multi-page host supplies a combined validator so pages that
   * are enabled decide what blocks submission. Must be referentially stable.
   */
  validate?: (draft: JobDraft) => string;
}

export interface UseJobFormResult {
  draft: JobDraft;
  error: string | null;
  submitting: boolean;
  patch: (partial: Partial<JobDraft>) => void;
  setTiming: (timing: JobTimingDraft) => void;
  addAttachments: (files: FileList | null) => void;
  removeAttachment: (id: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}

/**
 * Owns all New Job form state so the presentational component stays free of
 * business logic. Validation and submission side effects live here only.
 *
 * Staged attachments are split in two: the draft keeps display metadata while
 * `filesRef` keeps the actual `File` objects, handed to `onSubmit` so the host
 * can upload them after the job row exists. Attachment rows are never created
 * from metadata alone — a row without a file is a phantom.
 */
export function useJobForm(options: UseJobFormOptions): UseJobFormResult {
  const { initialValue, jobId, onSubmit, validate = validateJobDraft } = options;
  const [draft, setDraft] = useState<JobDraft>(() => ({ ...emptyJobDraft(), ...initialValue }));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const filesRef = useRef(new Map<string, File>());
  // Object URLs minted for staged image previews, keyed by attachment id so a
  // removed row releases exactly its own URL. Every entry is revoked on unmount
  // — a blob URL keeps the whole file alive in memory until it is.
  const previewUrlsRef = useRef(new Map<string, string>());

  useEffect(
    () => () => {
      for (const url of previewUrlsRef.current.values()) URL.revokeObjectURL(url);
      previewUrlsRef.current.clear();
    },
    [],
  );

  const patch = useCallback((partial: Partial<JobDraft>) => {
    setDraft((previous) => ({ ...previous, ...partial }));
  }, []);

  const setTiming = useCallback(
    (timing: JobTimingDraft) => {
      patch({ timing });
    },
    [patch],
  );

  const addAttachments = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const added = Array.from(files).map((file) => {
      const id = attachmentId(file.name);
      filesRef.current.set(id, file);
      // Images (thumbnails) and audio (inline waveform playback) are rendered
      // from the bytes before upload, so they get a preview URL; everything else
      // would only pin the file in memory for nothing.
      let previewUrl: string | undefined;
      if (file.type.startsWith("image/") || file.type.startsWith("audio/")) {
        previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.set(id, previewUrl);
      }
      return { id, name: file.name, size: file.size, type: file.type, previewUrl };
    });
    setDraft((previous) => ({ ...previous, attachments: previous.attachments.concat(added) }));
  }, []);

  const removeAttachment = useCallback(
    (id: string) => {
      const isPersisted = draft.attachments.find((candidate) => candidate.id === id)?.persisted;
      filesRef.current.delete(id);
      const previewUrl = previewUrlsRef.current.get(id);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrlsRef.current.delete(id);
      }
      setDraft((previous) => ({
        ...previous,
        attachments: previous.attachments.filter((candidate) => candidate.id !== id),
      }));
      // Persisted rows own a file on disk; drop them server-side too. Staged
      // entries that were never submitted only exist in this form's state.
      if (isPersisted && jobId) {
        void deleteAttachment(jobId, id).catch((deleteError) => {
          setError(
            deleteError instanceof Error
              ? deleteError.message
              : "Could not delete the attachment.",
          );
        });
      }
    },
    [draft.attachments, jobId],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting) return;
      const message = validate(draft);
      if (message) {
        setError(message);
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        await onSubmit(draft, new Map(filesRef.current));
        filesRef.current.clear();
      } catch (submitError) {
        setError(submitError instanceof Error ? submitError.message : "Submission failed. Your draft is kept.");
      } finally {
        setSubmitting(false);
      }
    },
    [draft, submitting, onSubmit, validate],
  );

  return { draft, error, submitting, patch, setTiming, addAttachments, removeAttachment, handleSubmit };
}
